'use client'

import { useMemo } from 'react'
import { X } from 'lucide-react'
import { cva } from 'class-variance-authority'
import {
  Chip,
  Stack,
  Input,
  InputWrapper,
  Skeleton,
  Checkbox,
  Callout,
  CalloutText,
  cn
} from 'design-system'
import { Search } from 'lucide-react'

const optionSearchSelectVariants = cva('overflow-y-auto', {
  variants: {
    size: {
      sm: 'h-[300px]',
      base: 'h-[450px]',
      l: 'h-[600px]'
    }
  },
  defaultVariants: {
    size: 'base'
  }
})

interface OptionSearchSelectProps<T> {
  options: T[]
  selectedOptions: T[]
  onSelectionChange: (selected: T[]) => void
  onRemove: (option: T) => void
  onSelect: (option: T) => void
  getOptionLabel: (option: T) => string
  getOptionId: (option: T) => string
  getOptionSubtext?: (option: T) => string | undefined
  getOptionLink?: (option: T) => string | undefined
  searchTerm: string
  onSearchChange: (term: string) => void
  maxSelection?: number
  placeholder?: string
  emptyStateMessage?: string
  noResultsMessage?: string
  selectedLabel?: string
  searchLabel?: string
  loading?: boolean
  size?: 'sm' | 'base' | 'l'
  headingLevel?: 'h2' | 'h3' | 'h4'
}

export function OptionSearchSelect<T>({
  options,
  selectedOptions,
  onSelectionChange: _onSelectionChange,
  onRemove,
  onSelect,
  getOptionLabel,
  getOptionId,
  getOptionSubtext,
  getOptionLink,
  searchTerm,
  onSearchChange,
  maxSelection = 5,
  placeholder = 'Search...',
  emptyStateMessage,
  noResultsMessage = 'No options found.',
  selectedLabel = 'Selected',
  searchLabel = 'Search',
  loading = false,
  size = 'base',
  headingLevel = 'h2'
}: OptionSearchSelectProps<T>) {
  const filteredOptions = useMemo(() => {
    const selectedIds = new Set(selectedOptions.map(getOptionId))
    const available = options.filter(
      option => !selectedIds.has(getOptionId(option))
    )

    if (!searchTerm) return available

    const lower = searchTerm.toLowerCase()
    return available.filter(option =>
      getOptionLabel(option).toLowerCase().includes(lower)
    )
  }, [options, selectedOptions, searchTerm, getOptionId, getOptionLabel])

  const canSelect = selectedOptions.length < maxSelection
  const HeadingLevel = headingLevel

  return (
    <Stack gap={4}>
      <div>
        <HeadingLevel className="mb-2 text-2xl font-medium">
          {selectedLabel}
        </HeadingLevel>
        {selectedOptions.length === 0 && emptyStateMessage && (
          <p className="py-2 text-neutral-600">{emptyStateMessage}</p>
        )}
        {selectedOptions.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {selectedOptions.map(option => {
              const link = getOptionLink?.(option)
              const optionId = getOptionId(option)

              return (
                <Chip
                  key={optionId}
                  color="blue"
                  size="base"
                  className="flex max-w-full items-center gap-x-1">
                  {link ? (
                    <a
                      href={link}
                      target="_blank"
                      rel="noreferrer"
                      className="max-w-[200px] truncate text-yd-blue transition-opacity hover:opacity-50 focus:opacity-50 active:opacity-80">
                      {getOptionLabel(option)}
                    </a>
                  ) : (
                    <span className="max-w-[200px] truncate">
                      {getOptionLabel(option)}
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={e => {
                      e.stopPropagation()
                      e.preventDefault()
                      onRemove(option)
                    }}
                    className="ml-1 shrink-0 rounded hover:opacity-70 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-300 focus-visible:ring-offset-1"
                    aria-label={`Remove ${getOptionLabel(option)}`}>
                    <X size={14} aria-hidden />
                  </button>
                </Chip>
              )
            })}
          </div>
        )}
      </div>

      <div>
        <HeadingLevel className="mb-2 text-2xl font-medium">
          {searchLabel}
        </HeadingLevel>
        <InputWrapper leftIconSlot={<Search size={16} />}>
          <Input
            type="text"
            placeholder={placeholder}
            value={searchTerm}
            onChange={e => onSearchChange(e.target.value)}
          />
        </InputWrapper>
      </div>

      <div className={cn(optionSearchSelectVariants({ size }))}>
        {loading && (
          <Stack gap={2}>
            {[1, 2, 3].map(i => (
              <Skeleton key={i} className="h-12" />
            ))}
          </Stack>
        )}
        {!loading && filteredOptions.length === 0 && (
          <Callout>
            <CalloutText>{noResultsMessage}</CalloutText>
          </Callout>
        )}
        {!loading &&
          filteredOptions.map(option => {
            const optionId = getOptionId(option)
            const isSelected = selectedOptions.some(
              selected => getOptionId(selected) === optionId
            )

            return (
              <div
                key={optionId}
                className={cn(
                  'flex items-center gap-x-2 rounded p-2 hover:bg-neutral-100',
                  {
                    'cursor-not-allowed opacity-50': !canSelect && !isSelected
                  }
                )}>
                <Checkbox
                  checked={isSelected}
                  disabled={!canSelect && !isSelected}
                  onChange={() => onSelect(option)}
                  ariaLabel={`Select ${getOptionLabel(option)}`}
                />
                <div className="flex-1">
                  <div className="font-medium">{getOptionLabel(option)}</div>
                  {getOptionSubtext && (
                    <div className="text-sm text-neutral-600">
                      {getOptionSubtext(option)}
                    </div>
                  )}
                </div>
              </div>
            )
          })}
      </div>
    </Stack>
  )
}
